import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

interface MarketPlaceState {
    collection: string
    filters: Record<string, string[]>
    search?: string
    sort?: string
}

interface MarketplaceUIState {
    filtersOpen: boolean
}

interface MarketplaceContextValue {
    state: MarketPlaceState
    setCollection: (collection: string) => void
    toggleFilter: (trait: string, value: string) => void
    clearFilters: (name?: string) => void
    setSearch: (value: string) => void
    setSort: (value: string) => void
    ui: MarketplaceUIState
    openUI: (key: keyof MarketplaceUIState) => void
    closeUI: (key: keyof MarketplaceUIState) => void
    toggleUI: (key: keyof MarketplaceUIState) => void
}

const MarketplaceContext = createContext<MarketplaceContextValue | null>(null)

function MarketplaceProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<MarketPlaceState>({
        collection: 'ape',
        filters: {},
    })

    const [ui, setUI] = useState<MarketplaceUIState>({
        filtersOpen: false,
    })

    const openUI = useCallback((key: keyof MarketplaceUIState) => {
        setUI((prev) => ({ ...prev, [key]: true }))
    }, [])

    const closeUI = useCallback((key: keyof MarketplaceUIState) => {
        setUI((prev) => ({ ...prev, [key]: false }))
    }, [])

    const toggleUI = useCallback((key: keyof MarketplaceUIState) => {
        setUI((prev) => ({ ...prev, [key]: !prev[key] }))
    }, [])

    const setCollection = useCallback((collection: string) => {
        setState((prev) => ({ ...prev, collection }))
    }, [])

    const toggleFilter = useCallback((trait: string, value: string) => {
        setState((prev) => {
            const current = prev.filters[trait] ?? []
            const exists = current.includes(value)
            const updated = exists ? current.filter((v) => v !== value) : [...current, value]

            const filters = { ...prev.filters }
            if (updated.length === 0) delete filters[trait]
            else filters[trait] = updated

            return { ...prev, filters }
        })
    }, [])

    const clearFilters = useCallback((name?: string) => {
        if (!name) setState((prev) => ({ ...prev, filters: {} }))
        else
            setState((prev) => {
                const filters = { ...prev.filters }
                delete filters[name]
                return { ...prev, filters }
            })
    }, [])

    const setSearch = useCallback((value: string) => {
        setState((prev) => ({ ...prev, search: value }))
    }, [])

    const clearSearch = useCallback(() => {
        setState((prev) => ({ ...prev, search: '' }))
    }, [])

    const setSort = useCallback((value: string) => {
        setState((prev) => ({ ...prev, sort: value }))
    }, [])

    const clearSort = useCallback(() => {
        setState((prev) => ({ ...prev, sort: '' }))
    }, [])

    useEffect(() => {
        clearFilters()
        clearSearch()
        clearSort()
    }, [state.collection])

    return (
        <MarketplaceContext.Provider
            value={{
                state,
                setCollection,
                toggleFilter,
                clearFilters,
                setSearch,
                setSort,
                ui,
                openUI,
                closeUI,
                toggleUI,
            }}
        >
            {children}
        </MarketplaceContext.Provider>
    )
}

function useMarketplace() {
    const ctx = useContext(MarketplaceContext)
    if (!ctx) throw new Error('useMarketplace must be used within MarketplaceProvider')
    return ctx
}

export { MarketplaceProvider, useMarketplace }
